/* eslint-disable */

import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { useTheme } from '@mui/material/styles';
import React, { useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import useMaskAmount from 'app-components/hooks/privacyMode/useMaskAmount';
import { useAppSelector } from './redux';
import selectPrivacyModeEnabled from 'app-components/hooks/privacyMode/selectPrivacyModeEnabled';

// custom hook for creating XY amCharts in Simplifi fashion
export default function useXYChart(divId, useDate) {
  const chart = useRef(null);
  const loadingIndicatorRef = useRef(null);
  const theme = useTheme();

  const [xAxis, setXAxis] = useState(null);
  const [yAxis, setYAxis] = useState(null);

  const privacyModeEnabled = useAppSelector(selectPrivacyModeEnabled);
  // const [loadingContainer, setLoadingContainer] = useState(null);  // disabled until we get the eventDispatcher bug under control

  // builds html string for a series to be added to a tooltip, uses series.dataFields.categoryX, series.dataFields.valueY, and series.dataFields.custom for color
  const buildTooltipSeriesInfoForSimpleBarChart = useCallback(
    (series, compact = false, percentage = false, customStyles = '') =>
      `<div style="${customStyles} width: ${
        compact ? 130 : 172
      }px;display: flex;flex-direction: row;align-items: center;color: ${theme.palette.text.secondary}" >
      <div style=";background: {${series.dataFields.custom}};min-width: 12px;min-height: 12px;border-radius: 50%;margin-right: 3px;" ></div>
      <span style="flex-grow: 1;color: inherit;display: flex;justify-content: space-between;font-size: 14px;min-width: ${
        compact ? 0 : 140
      }px" >
        <span style="flex: 2;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;text-transform: capitalize;" id="tooltip_category" >{${
          series.dataFields.categoryX
        }}</span>
        <span style="flex: 1;text-align: right;" id="tooltip_amount" >${percentage ? '' : '$'}{${
          series.dataFields.valueY
        }}${percentage ? '%' : ''}</span>
      </span>
    </div>`,
    [theme],
  );

  // builds html string for a series to be added to a tooltip, uses series.name, series.dataFields.valueY, and series.fill
  const buildTooltipSeriesInfo = useCallback(
    (series, compact = false, percentage = false, customStyles = '') =>
      `<div style="${customStyles} width: ${
        compact ? 130 : 172
      }px;display: flex;flex-direction: row;align-items: center;color: ${theme.palette.text.secondary}" >
      <div style=";background: ${series.fill};min-width: 12px;min-height: 12px;border-radius: 50%;margin-right: 3px;" ></div>
      <span style="flex-grow: 1;color: inherit;display: flex;justify-content: space-between;font-size: 14px;min-width: ${
        compact ? 0 : 140
      }px" >
        <span style="flex: 2;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;text-transform: capitalize;" id="tooltip_category" >${
          series.name
        }</span>
        <span style="flex: 1;text-align: right;" id="tooltip_amount" >${percentage ? '' : '$'}${
          privacyModeEnabled ? '***' : `{${series.dataFields.valueY}}`
        }${percentage ? '%' : ''}</span>
      </span>
    </div>`,
    [theme, privacyModeEnabled],
  );

  // builds a tooltip on a given amCharts object
  const buildTooltipOn = useCallback(
    (paramObj, sideOfPointer = false, hideDefaultShadow = true) => {
      const obj = paramObj;

      if (!sideOfPointer) {
        obj.tooltip = new am4core.Tooltip();
      }

      obj.tooltip.getFillFromObject = false;
      obj.tooltip.background.fillOpacity = 0.94;
      obj.tooltip.background.fill = theme.palette.background.paper;
      obj.tooltip.label.padding(0, 0, 0, 0);
      obj.tooltip.background.strokeWidth = 1;
      obj.tooltip.background.stroke = theme.palette.border.main;
      obj.tooltip.background.cornerRadius = theme.shape.borderRadius * 2;
      obj.tooltip.background.valign = 'middle';
      obj.tooltip.zIndex = Infinity;
      obj.tooltip.interactionsEnabled = false;
      obj.tooltip.showInViewport = true;
      if (!sideOfPointer) {
        obj.tooltip.background.pointerLength = 0;
        obj.tooltip.pointerOrientation = 'vertical';
      }
      if (!hideDefaultShadow) {
        const shadow = obj.tooltip.background.filters.getIndex(0);
        shadow.dx = 0;
        shadow.dy = 0;
        shadow.blur = 2;
        shadow.spread = 0;
        shadow.opacity = 0.5;
        shadow.color = am4core.color(theme.applyOpacityToHex(theme.palette.backdrop.dark, 0.5));
      } else {
        obj.tooltip.background.filters.clear();
      }

      return obj;
    },
    [theme],
  );

  // creates a range between categories in the xAxis, returns the range
  const createCategoryRange = useCallback(
    (startCategory, endCategory = null) => {
      const range = xAxis.axisRanges.create();
      if (useDate) {
        range.date = startCategory;
        range.endDate = endCategory || startCategory;
      } else {
        range.category = startCategory;
        range.endCategory = endCategory || startCategory;
      }
      range.label.disabled = true;
      range.axisFill.interactionsEnabled = true;
      range.axisFill.isMeasured = true;

      return range;
    },
    [useDate, xAxis],
  );

  // builds a legend on the chart, disposes old legend
  const buildLegend = useCallback(() => {
    const tempChart = chart.current;
    if (!tempChart) return;

    tempChart.legend?.dispose(); // clean up old legend if it exists

    tempChart.legend = new am4charts.Legend();
    tempChart.legend.useDefaultMarker = true;
    tempChart.legend.labels.template.fill = am4core.color(theme.palette.text.primary);
    tempChart.legend.markers.template.width = 16;
    tempChart.legend.markers.template.height = 16;
    tempChart.legend.markers.template.children.getIndex(0).cornerRadius(2, 2, 2, 2);
    tempChart.legend.interactionsEnabled = false;

    // return tempChart.legend;
  }, [chart, theme.palette.text.primary]);

  // builds a default, active, and inactive state on a given amCharts object
  // also handles lightening of color and showing tooltip for the given states
  const makeSelectionStates = useCallback(
    (paramObj, color, inactiveColor) => {
      const defaultState = paramObj.states.getKey('default');
      if (defaultState) {
        // in case the object doesn't have a default state, bullets for some reason are funky
        if (color) {
          defaultState.properties.fill = color;
          defaultState.properties.stroke = color;
        }
        defaultState.properties.showTooltipOn = 'hover';
      }

      const activeState = paramObj.states.create('active');
      if (color) {
        activeState.properties.fill = color;
        activeState.properties.stroke = color;
      }
      activeState.properties.showTooltipOn = 'always';

      const inactiveState = paramObj.states.create('inactive');
      if (inactiveColor) {
        inactiveState.properties.fill = inactiveColor;
        inactiveState.properties.stroke = inactiveColor;
      } else if (!inactiveColor && color) {
        inactiveState.properties.fill = color.lighten(0.8);
        inactiveState.properties.stroke = color.lighten(0.8);
      }
      inactiveState.properties.showTooltipOn = 'hover';

      const archivedState = paramObj.states.create('archived');
      archivedState.properties.fill = am4core.color(theme.palette.chart.grid);
      archivedState.properties.stroke = am4core.color(theme.palette.chart.grid);

      return { defaultState, activeState, inactiveState }; // return states so they can be further adjusted
    },
    [theme],
  );

  const maskAmount = useMaskAmount();

  // main effect building the chart
  useLayoutEffect(() => {
    am4core.useTheme(am4themes_animated);

    const tempChart = am4core.create(divId, am4charts.XYChart);

    // needed for tooltips
    tempChart.cursor = new am4charts.XYCursor();
    tempChart.cursor.lineX.disabled = true;
    tempChart.cursor.lineY.disabled = true;
    tempChart.cursor.behavior = 'none';

    let categoryAxis;
    if (useDate) {
      categoryAxis = tempChart.xAxes.push(new am4charts.DateAxis());
      categoryAxis.dataFields.dateX = 'date';
    } else {
      categoryAxis = tempChart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'category';
    }
    categoryAxis.tooltip.disabled = true;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.grid.template.location = 0.5;
    categoryAxis.renderer.grid.template.strokeOpacity = 0;
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;
    categoryAxis.renderer.ticks.template.disabled = false;
    categoryAxis.renderer.line.opacity = 1;
    if (!useDate) {
      categoryAxis.renderer.minGridDistance = 20;
    }
    categoryAxis.renderer.labels.template.fontSize = '0.75rem';
    categoryAxis.renderer.labels.template.fontWeight = 400;
    categoryAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
    categoryAxis.renderer.labels.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

    // We need to dispose these custom events later
    let labelOverDisposer;
    let labelOutDisposer;
    if (!tempChart.isDisposed()) {
      // handles hovering the category labels to trigger a hover over the y axis
      labelOverDisposer = categoryAxis.renderer.labels.template.events.on('over', (e) => {
        if (tempChart.isDisposed()) return;
        let point;
        if (useDate) {
          point = categoryAxis.dateToPoint(e.target.dataItem.date);
        } else {
          point = categoryAxis.categoryToPoint(e.target.dataItem.category);
        }
        if (!tempChart.cursor || tempChart.isDisposed()) return;
        tempChart.cursor.triggerMove(point, 'soft');
      });
      labelOutDisposer = categoryAxis.renderer.labels.template.events.on('out', (e) => {
        if (tempChart.isDisposed()) return;
        let point;
        if (useDate) {
          point = categoryAxis.dateToPoint(e.target.dataItem.date);
        } else {
          point = categoryAxis.categoryToPoint(e.target.dataItem.category);
        }
        if (!tempChart.cursor || tempChart.isDisposed()) return;
        tempChart.cursor.triggerMove(point, 'none');
      });
    }

    const valueAxis = tempChart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.grid.template.disabled = true;
    valueAxis.tooltip.disabled = true;
    valueAxis.renderer.line.opacity = 0;
    valueAxis.renderer.line.zIndex = -1;
    valueAxis.renderer.ticks.template.disabled = true;
    valueAxis.renderer.labels.template.fontSize = '0.75rem';
    valueAxis.renderer.labels.template.fontWeight = 400;
    valueAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
    valueAxis.numberFormatter = new am4core.NumberFormatter();
    valueAxis.numberFormatter.smallNumberThreshold = 0;
    valueAxis.numberFormatter.numberFormat = '$#,###';
    valueAxis.renderer.baseGrid.stroke = am4core.color(theme.palette.chart.today);
    valueAxis.renderer.baseGrid.zIndex = 1;
    valueAxis.renderer.minGridDistance = 25;
    valueAxis.renderer.labels.template.adapter.add('textOutput', (text) =>
      typeof text === 'string' ? maskAmount(text) : text,
    );
    valueAxis.extraMax = 0.1;
    valueAxis.calculateTotals = true;

    tempChart.maxZoomLevel = 1;
    tempChart.zoomOutButton.disabled = true;

    // TODO: loading indicator for graph

    const loadingIndicator = tempChart.tooltipContainer.createChild(am4core.Container);
    loadingIndicator.hide();
    loadingIndicator.background.fill = am4core.color(theme.palette.background.default);
    loadingIndicator.background.fillOpacity = 0.8;
    loadingIndicator.width = am4core.percent(100);
    loadingIndicator.height = am4core.percent(100);

    const loader = loadingIndicator.createChild(am4core.Container);
    const loader1 = loader.createChild(am4core.Sprite);
    const loader2 = loader.createChild(am4core.Sprite);
    loader1.path =
      'M129 65C129 100.346 100.346 129 65 129C29.6538 129 1 100.346 1 65C1 29.6538 29.6538 1 65 1C100.346 1 129 29.6538 129 65ZM10.9573 65C10.9573 94.847 35.153 119.043 65 119.043C94.847 119.043 119.043 94.847 119.043 65C119.043 35.153 94.847 10.9573 65 10.9573C35.153 10.9573 10.9573 35.153 10.9573 65Z';
    loader1.fill = am4core.color('#C9D4F3');
    loader2.path =
      'M97.0054 15.4099C98.4964 13.0997 101.592 12.4206 103.779 14.0867C114.46 22.2216 122.349 33.5413 126.26 46.4728C130.171 59.4042 129.878 73.1984 125.495 85.889C124.598 88.488 121.645 89.6383 119.123 88.5416C116.602 87.4448 115.471 84.5168 116.331 81.9052C119.793 71.3925 119.956 60.0261 116.729 49.3553C113.501 38.6845 107.067 29.3134 98.3587 22.4816C96.1954 20.7844 95.5143 17.7202 97.0054 15.4099Z';
    loader2.fill = am4core.color(theme.palette.primary.main);
    loader.align = 'center';
    loader.valign = 'middle';

    loader
      .animate(
        [
          {
            from: 0,
            to: 360,
            property: 'rotation',
          },
        ],
        1500,
      )
      .loop();

    loadingIndicatorRef.current = loadingIndicator;
    chart.current = tempChart;
    setXAxis(categoryAxis);
    setYAxis(valueAxis);

    return () => {
      // Dispose custom events before disposing the chart
      if (labelOverDisposer?.dispose) labelOverDisposer.dispose();
      if (labelOutDisposer?.dispose) labelOutDisposer.dispose();

      // Clear default chart events before disposing the chart
      if (tempChart.responsive) {
        tempChart.responsive.enabled = false;
        tempChart.responsive.rules.clear();
      }
      tempChart.dispose();
    };
  }, [divId, theme, useDate, maskAmount]);

  const displayLoadingIndicator = useCallback(
    (isLoading) => {
      if (loadingIndicatorRef.current !== null) {
        if (isLoading) {
          loadingIndicatorRef.current.show();
        } else {
          loadingIndicatorRef.current.hide();
        }
      }
    },
    [loadingIndicatorRef],
  );

  return {
    chart: chart.current,
    xAxis,
    yAxis,
    buildTooltipOn,
    buildTooltipSeriesInfo,
    buildTooltipSeriesInfoForSimpleBarChart,
    buildLegend,
    createCategoryRange,
    makeSelectionStates,
    displayLoadingIndicator,
  };
}

// ================================================================================================================== //
//                                                   Usage/Example                                                    //
// ================================================================================================================== //

const exampleData = [
  { category: 'Jan', simplifi: 100, quicken: 150 },
  { category: 'Feb', simplifi: 150, quicken: 170 },
  { category: 'Mar', simplifi: 250, quicken: 200 },
];

export function ExampleCharts(_props) {
  const { chart: barChart, buildLegend: buildBarChartLegend } = useXYChart('example-bar-chart-div');
  const {
    chart: multilineChart,
    buildTooltipOn: buildMultilineChartTooltipOn,
    buildTooltipSeriesInfo: buildMultilineChartTooltipSeriesInfo,
  } = useXYChart('example-multiline-chart-div');

  const createColumnSeries = useCallback(
    (name, field) => {
      const series = barChart.series.push(new am4charts.ColumnSeries());
      series.name = name;
      series.dataFields.valueY = field;
      series.dataFields.categoryX = 'category'; // needs to be category to work with the axis
    },
    [barChart],
  );

  const createLineSeries = useCallback(
    (name, field) => {
      const series = multilineChart.series.push(new am4charts.LineSeries());
      series.name = name;
      series.dataFields.valueY = field;
      series.dataFields.categoryX = 'category'; // needs to be category to work with the axis

      buildMultilineChartTooltipOn(series);
      series.tooltipHTML = buildMultilineChartTooltipSeriesInfo(series);
    },
    [multilineChart, buildMultilineChartTooltipOn, buildMultilineChartTooltipSeriesInfo],
  );

  useLayoutEffect(() => {
    if (!barChart || !multilineChart) return;

    createColumnSeries('simplifi', 'simplifi');
    createColumnSeries('quicken', 'quicken');
    barChart.data = exampleData; // if the data is going to be changing it should get set in a separate useLayoutEffect
    buildBarChartLegend();

    createLineSeries('simplifi', 'simplifi');
    createLineSeries('quicken', 'quicken');
    multilineChart.data = exampleData; // if the data is going to be changing it should get set in a separate useLayoutEffect
  }, [barChart, multilineChart, buildBarChartLegend, createColumnSeries, createLineSeries]);

  const chartStyle = useMemo(() => ({ width: 500, height: 200 }), []);

  return (
    <div style={{ display: 'flex' }}>
      <div>
        <p>Bar Chart, using legend helpers</p>
        <div style={chartStyle} id='example-bar-chart-div' />
      </div>
      <div>
        <p>Multiline Chart, using tooltip helpers</p>
        <div style={chartStyle} id='example-multiline-chart-div' />
      </div>
    </div>
  );
}
